import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
    ArrowRight, Shield, Sparkles, Gavel, Scale,
    Radar, BellRing, FileSearch, ListChecks, Quote, Landmark,
} from "lucide-react";
import { EuStars } from "../components/EuStars";
import DemoShowcase from "../components/DemoShowcase";
import SiteFooter from "../components/SiteFooter";
import PublicNav from "../components/PublicNav";
import { useLang, LangSuggestion, trackPublic } from "../lib/i18n";

const HERO_IMG = "/img/hero-colonnade.webp";
const INTERIOR_IMG = "/img/institution-interior.webp";

const META = {
    en: {
        title: "EuroLaw Hub | EU Legal Research Workspace",
        description:
            "EuroLaw Hub: EU legal research workspace for qualified counsel. GDPR, AI Act, DORA, NIS2 and CJEU case law. Every citation traceable to its primary source.",
    },
    tr: {
        title: "EuroLaw Hub | AB Hukuku Araştırma Platformu",
        description:
            "EuroLaw Hub: AB hukuku ve Türk hukuku için araştırma platformu. GDPR, KVKK, Yapay Zeka Yasası ve ABAD içtihadı. Her atıf, resmi birincil kaynağına kadar doğrulanabilir.",
    },
};

/* Scroll reveal: fades in once when the element enters the viewport. */
function Reveal({ children, className = "", delay = 0 }) {
    const ref = useRef(null);
    const [shown, setShown] = useState(false);
    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
            setShown(true);
            return;
        }
        const obs = new IntersectionObserver(
            ([e]) => { if (e.isIntersecting) { setShown(true); obs.disconnect(); } },
            { threshold: 0.12 }
        );
        obs.observe(el);
        return () => obs.disconnect();
    }, []);
    return (
        <div ref={ref} className={`reveal ${shown ? "reveal-in" : ""} ${className}`}
            style={delay ? { transitionDelay: `${delay}ms` } : undefined}>
            {children}
        </div>
    );
}

/* Fires a landing_section_view event once per page view when seen. */
function useSectionView(section) {
    const ref = useRef(null);
    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        const obs = new IntersectionObserver(([e]) => {
            if (e.isIntersecting) {
                trackPublic("landing_section_view", { section });
                obs.disconnect();
            }
        }, { threshold: 0.3 });
        obs.observe(el);
        return () => obs.disconnect();
    }, [section]);
    return ref;
}

function useSeo(lang) {
    useEffect(() => {
        const meta = META[lang] || META.en;
        document.title = meta.title;
        let desc = document.querySelector('meta[name="description"]');
        const prevDesc = desc ? desc.getAttribute("content") : null;
        if (desc) desc.setAttribute("content", meta.description);
        document.documentElement.lang = lang;
        const origin = window.location.origin;
        const links = [
            { rel: "alternate", hreflang: "en", href: `${origin}/` },
            { rel: "alternate", hreflang: "tr", href: `${origin}/tr` },
            { rel: "alternate", hreflang: "x-default", href: `${origin}/` },
            { rel: "canonical", href: lang === "tr" ? `${origin}/tr` : `${origin}/` },
        ].map((spec) => {
            const el = document.createElement("link");
            el.setAttribute("rel", spec.rel);
            if (spec.hreflang) el.setAttribute("hreflang", spec.hreflang);
            el.setAttribute("href", spec.href);
            el.setAttribute("data-elh-seo", "1");
            document.head.appendChild(el);
            return el;
        });
        return () => {
            links.forEach((el) => el.remove());
            document.documentElement.lang = "en";
            if (desc && prevDesc) desc.setAttribute("content", prevDesc);
            document.title = META.en.title;
        };
    }, [lang]);
}

export default function Landing({ forcedLang }) {
    const { lang: ctxLang, setLangLocal, T } = useLang();
    const lang = forcedLang === "tr" ? "tr" : "en";
    useEffect(() => {
        if (ctxLang !== lang) setLangLocal(lang);
        if (lang === "tr") {
            try { localStorage.setItem("elh_lang", "tr"); } catch { /* ignore */ }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lang]);
    useSeo(lang);
    const t = (en, tr) => (lang === "tr" ? tr : en);

    return (
        <div className="min-h-screen bg-[#F5F0E8] text-[#0D1B3E]">
            <LangSuggestion />
            <PublicNav />

            {/* Hero */}
            <section className="relative overflow-hidden bg-[#0D1B3E]" data-testid="hero-section">
                <img src={HERO_IMG} alt="" width="1264" height="848" fetchpriority="high"
                    className="kenburns absolute inset-0 w-full h-full object-cover" aria-hidden="true" />
                <div className="absolute inset-0 bg-gradient-to-b from-[#0D1B3E]/90 via-[#0D1B3E]/86 to-[#0D1B3E]/94" />
                <div className="relative container-edit pt-24 pb-28 lg:pt-36 lg:pb-44 text-[#F5F0E8]">
                    <div className="max-w-3xl">
                        <div className="flex items-center gap-3 mb-8 animate-fade-up">
                            <EuStars size={36} />
                            <span className="text-[11px] font-semibold uppercase tracking-[0.28em]" style={{ color: "#C9A84C" }}>
                                {t("EU Legal Research Workspace", "AB Hukuku Araştırma Platformu")}
                            </span>
                        </div>
                        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight animate-fade-up" style={{ animationDelay: "60ms" }}>
                            {t("Precision in", "AB hukuku araştırmasında")}{" "}
                            <span className="block italic" style={{ color: "#C9A84C" }}>
                                {t("EU legal research.", "hassasiyet.")}
                            </span>
                        </h1>
                        <p className="mt-7 max-w-2xl text-lg leading-relaxed text-[#F5F0E8]/80 animate-fade-up" style={{ animationDelay: "140ms" }}>
                            {t(
                                "Research GDPR, the AI Act, DORA, NIS2 and CJEU case law with answers grounded in the primary sources. Every citation traceable to the article, recital or paragraph it relies on.",
                                "GDPR, Yapay Zeka Yasası, DORA, NIS2 ve ABAD içtihadını birincil kaynaklara dayalı yanıtlarla araştırın. Her atıf, dayandığı maddeye, gerekçeye veya paragrafa kadar izlenebilir."
                            )}
                        </p>
                        <div className="mt-10 flex flex-wrap gap-3 animate-fade-up" style={{ animationDelay: "220ms" }}>
                            <Link to="/signup" data-testid="hero-cta-launch" className="btn-primary"
                                onClick={() => trackPublic("landing_cta_click", { section: "hero", lang })}>
                                {t("Start Free Trial", "Ücretsiz Denemeyi Başlat")} <ArrowRight size={16} />
                            </Link>
                            <a href="#demo" data-testid="hero-cta-try-free" className="btn-secondary"
                                style={{ color: "#F5F0E8", borderColor: "#C9A84C" }}>
                                {t("See it in action", "Nasıl çalıştığını görün")}
                            </a>
                        </div>
                        <div className="mt-14 flex flex-wrap items-center gap-x-8 gap-y-3 text-xs uppercase tracking-[0.22em] text-[#F5F0E8]/55 animate-fade-up" style={{ animationDelay: "300ms" }}>
                            <span>GDPR</span>
                            <span>· {t("AI Act", "Yapay Zeka Yasası")}</span>
                            <span>· KVKK</span>
                            <span>· DORA</span>
                            <span>· NIS2</span>
                            <span>· {t("CJEU case law", "ABAD içtihadı")}</span>
                        </div>
                    </div>
                </div>
            </section>

            {/* Interactive recorded demo */}
            <DemoShowcase lang={lang} />

            {/* Module showcase */}
            <section className="container-edit py-24 lg:py-32" data-testid="modules-section">
                <Reveal>
                    <div className="max-w-2xl">
                        <div className="label-eyebrow">{t("The platform", "Platform")}</div>
                        <h2 className="font-display text-3xl lg:text-4xl mt-3 leading-[1.1] tracking-tight">
                            {t("One workspace for EU legal work.", "AB hukuku çalışmaları için tek bir platform.")}
                        </h2>
                        <p className="mt-5 text-base leading-relaxed text-[#0D1B3E]/75">
                            {t(
                                "Generic chatbots invent citations. EuroLaw Hub answers from primary sources only, and tells you exactly which article, recital or paragraph it relied on.",
                                "Genel sohbet robotları atıf uydurur. EuroLaw Hub yalnızca birincil kaynaklardan yanıt verir ve hangi maddeye, gerekçeye veya paragrafa dayandığını açıkça gösterir."
                            )}
                        </p>
                    </div>
                </Reveal>
                <div className="mt-14 grid gap-px bg-[#0D1B3E]/12 border border-[#0D1B3E]/12 sm:grid-cols-2 lg:grid-cols-3">
                    <ModuleCard icon={Sparkles} title={t("Research Assistant", "Araştırma Asistanı")}
                        body={t("Ask a question in plain language. Receive a structured answer with inline citations to the Official Journal and CJEU case law.",
                            "Sorunuzu günlük dille sorun. AB Resmî Gazetesi ve ABAD içtihadına atıflı, yapılandırılmış bir yanıt alın.")} delay={0} />
                    <ModuleCard icon={Shield} title={t("Compliance workspaces", "Uyum çalışma alanları")}
                        body={t("Dedicated AI Act, GDPR, DORA and NIS2 workspaces. Map obligations, run gap checklists and keep your progress.",
                            "Yapay Zeka Yasası, GDPR, DORA ve NIS2 için özel çalışma alanları. Yükümlülükleri haritalayın, eksik analizi yapın, ilerlemenizi kaydedin.")} delay={60} />
                    <ModuleCard icon={Gavel} title={t("Case Law Explorer", "İçtihat Gezgini")}
                        body={t("Landmark CJEU rulings with holdings, tests applied and practical takeaways. Compare any two cases side by side.",
                            "Önemli ABAD kararları; hüküm, uygulanan testler ve pratik çıkarımlarla. İki kararı yan yana karşılaştırın.")} delay={120} />
                    <ModuleCard icon={Scale} title={t("Treaty Explorer", "Antlaşma Gezgini")}
                        body={t("Read the TEU, TFEU and the Charter article by article, with analytical and case-law perspectives for each provision.",
                            "ABA, ABİHA ve Temel Haklar Şartı'nı madde madde okuyun; her hüküm için analitik ve içtihat perspektifleriyle.")} delay={0} />
                    <ModuleCard icon={Radar} title={t("Regulatory Intelligence", "Mevzuat Takibi")}
                        body={t("Track 30+ EU instruments: adoption, entry into force, phased application dates and cross-regulation interplay.",
                            "30'dan fazla AB düzenlemesini izleyin: kabul, yürürlük, kademeli uygulama tarihleri ve düzenlemeler arası etkileşim.")} delay={60} />
                    <ModuleCard icon={BellRing} title={t("Watchdogs & Bulletins", "Takipçiler ve Bültenler")}
                        body={t("Subscribe to a regulation or topic and receive a source-grounded bulletin when something material is published.",
                            "Bir düzenlemeye veya konuya abone olun; önemli bir gelişme yayımlandığında kaynak temelli bir bülten alın.")} delay={120} />
                </div>
            </section>

            <TurkiyeSection lang={lang} t={t} />

            {/* How it works */}
            <section className="bg-[#0D1B3E] text-[#F5F0E8] relative overflow-hidden" data-testid="how-it-works-section">
                <img src={INTERIOR_IMG} alt="" width="1264" height="848" loading="lazy"
                    className="absolute inset-0 w-full h-full object-cover" aria-hidden="true" />
                <div className="absolute inset-0 bg-[#0D1B3E]/90" />
                <div className="relative container-edit py-24 lg:py-32">
                    <Reveal>
                        <div className="label-eyebrow">{t("How it works", "Nasıl çalışır")}</div>
                        <h2 className="font-display text-3xl lg:text-4xl mt-3 leading-[1.1] tracking-tight max-w-xl">
                            {t("From question to cited answer in three steps.", "Sorudan atıflı yanıta üç adımda.")}
                        </h2>
                    </Reveal>
                    <div className="mt-14 grid gap-10 lg:grid-cols-3">
                        <Step n="01" icon={FileSearch} title={t("Ask or upload", "Sorun veya yükleyin")}
                            body={t("Pose a research question, or upload your own documents for the assistant to read alongside the EU corpus.",
                                "Bir araştırma sorusu sorun veya asistanın AB külliyatıyla birlikte okuması için kendi belgelerinizi yükleyin.")} delay={0} />
                        <Step n="02" icon={Quote} title={t("Grounded analysis", "Kaynak temelli analiz")}
                            body={t("The assistant reasons over the Official Journal texts, CJEU judgments and treaty provisions relevant to your question.",
                                "Asistan, sorunuzla ilgili Resmî Gazete metinleri, ABAD kararları ve antlaşma hükümleri üzerinden akıl yürütür.")} delay={80} />
                        <Step n="03" icon={ListChecks} title={t("Verify every citation", "Her atfı doğrulayın")}
                            body={t("Each statement carries its source. Follow the citation to EUR-Lex or Curia and confirm the wording yourself.",
                                "Her ifade kaynağını taşır. Atfı EUR-Lex veya Curia üzerinden izleyin ve metni kendiniz doğrulayın.")} delay={160} />
                    </div>
                    <Reveal delay={120}>
                        <div className="mt-14">
                            <Link to="/signup" data-testid="how-cta-signup" className="btn-primary"
                                onClick={() => trackPublic("landing_cta_click", { section: "how_it_works", lang })}>
                                {t("Start Free Trial", "Ücretsiz Denemeyi Başlat")} <ArrowRight size={16} />
                            </Link>
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* Pricing teaser */}
            <section className="container-edit py-24 lg:py-32" data-testid="pricing-teaser-section">
                <Reveal>
                    <div className="max-w-2xl">
                        <div className="label-eyebrow">{t("Pricing", "Fiyatlandırma")}</div>
                        <h2 className="font-display text-3xl lg:text-4xl mt-3 leading-[1.1] tracking-tight">
                            {t("Clear plans. A full trial first.", "Net planlar. Önce tam kapsamlı deneme.")}
                        </h2>
                        <p className="mt-5 text-base leading-relaxed text-[#0D1B3E]/75">
                            {t("Every account starts with a 7-day free trial of the complete platform. No credit card required.",
                                "Her hesap, platformun tamamını kapsayan 7 günlük ücretsiz denemeyle başlar. Kredi kartı gerekmez.")}
                        </p>
                        {lang === "tr" && (
                            <p className="mt-3 text-xs text-[#0D1B3E]/60" data-testid="tr-currency-note">
                                Fiyatlar euro (EUR) cinsindendir; KDV ödeme adımında hesaplanır.
                            </p>
                        )}
                    </div>
                </Reveal>
                <div className="mt-12 grid gap-6 lg:grid-cols-2 max-w-4xl">
                    <Reveal>
                        <div className="card-flat p-8 h-full" data-testid="teaser-plan-pro">
                            <div className="label-eyebrow">{t("Professional", "Profesyonel")}</div>
                            <div className="mt-4 flex items-baseline gap-2">
                                <span className="font-display text-4xl">€49</span>
                                <span className="text-sm text-[#0D1B3E]/60">{t("per month", "aylık")}</span>
                            </div>
                            <p className="mt-4 text-sm leading-relaxed text-[#0D1B3E]/75">
                                {t("For individual practitioners. Full research suite, compliance workspaces, document uploads and monthly research credits.",
                                    "Bireysel avukatlar ve danışmanlar için. Tam araştırma paketi, uyum çalışma alanları, belge yükleme ve aylık araştırma kredileri.")}
                            </p>
                        </div>
                    </Reveal>
                    <Reveal delay={80}>
                        <div className="card-flat p-8 h-full" data-testid="teaser-plan-team">
                            <div className="label-eyebrow">{t("Team", "Ekip")}</div>
                            <div className="mt-4 flex items-baseline gap-2">
                                <span className="font-display text-4xl">€199</span>
                                <span className="text-sm text-[#0D1B3E]/60">{t("per month, 3 seats included", "aylık, 3 kullanıcı dahil")}</span>
                            </div>
                            <p className="mt-4 text-sm leading-relaxed text-[#0D1B3E]/75">
                                {t("For legal teams and in-house counsel. Shared workspace, seat management and a pooled credit budget. Extra seats €49 per month each.",
                                    "Hukuk ekipleri ve şirket içi hukuk birimleri için. Ortak çalışma alanı, kullanıcı yönetimi ve ortak kredi havuzu. Ek kullanıcı aylık €49.")}
                            </p>
                        </div>
                    </Reveal>
                </div>
                <Reveal delay={120}>
                    <div className="mt-10">
                        <Link to="/pricing" data-testid="teaser-cta-pricing" className="btn-secondary">
                            {t("Compare plans in detail", "Planları ayrıntılı karşılaştırın")} <ArrowRight size={16} />
                        </Link>
                    </div>
                </Reveal>
            </section>

            <hr className="gold-rule container-edit" />

            {/* Final CTA */}
            <section className="container-edit py-24 lg:py-32 text-center" data-testid="final-cta-section">
                <Reveal>
                    <div className="mx-auto max-w-2xl">
                        <EuStars size={36} className="mx-auto" />
                        <h2 className="font-display text-3xl lg:text-5xl mt-6 leading-[1.08] tracking-tight">
                            {t("Begin with a question", "Doğru yanıtlanması gereken")}
                            <span className="block italic" style={{ color: "#C9A84C" }}>
                                {t("you need answered properly.", "bir soruyla başlayın.")}
                            </span>
                        </h2>
                        <p className="mt-6 text-base leading-relaxed text-[#0D1B3E]/75">
                            {t("Seven days of full access. Your work is saved, your citations are verifiable, and you can leave at any time.",
                                "Yedi gün tam erişim. Çalışmalarınız kaydedilir, atıflarınız doğrulanabilir ve dilediğiniz zaman ayrılabilirsiniz.")}
                        </p>
                        <div className="mt-10">
                            <Link to="/signup" data-testid="final-cta-signup" className="btn-primary"
                                onClick={() => trackPublic("landing_cta_click", { section: "final", lang })}>
                                {t("Start Free Trial", "Ücretsiz Denemeyi Başlat")} <ArrowRight size={16} />
                            </Link>
                        </div>
                    </div>
                </Reveal>
            </section>

            <SiteFooter />
        </div>
    );
}

/* Compact, understated Türkiye layer section (not in the hero). */
function TurkiyeSection({ lang, t }) {
    const ref = useSectionView("turkiye_layer");
    const items = [
        t("KVKK and GDPR side by side, with the differences that matter in practice.",
            "KVKK ve GDPR yan yana; uygulamada fark yaratan noktalarla."),
        t("Dual-compliance checking for organisations operating in both jurisdictions.",
            "Her iki hukuk düzeninde faaliyet gösteren kuruluşlar için çift uyum kontrolü."),
        t("Comparative research across EU and Turkish law in a single question.",
            "Tek bir soruda AB hukuku ile Türk hukuku arasında karşılaştırmalı araştırma."),
        t("ECtHR case law concerning Türkiye, alongside CJEU jurisprudence.",
            "ABAD içtihadının yanında, Türkiye ile ilgili AİHM kararları."),
    ];
    return (
        <section ref={ref} className="bg-[#EFE8DB] border-y border-[#0D1B3E]/10" data-testid="turkiye-section">
            <div className="container-edit py-16 lg:py-20">
                <div className="grid gap-10 lg:grid-cols-12 items-start">
                    <div className="lg:col-span-5">
                        <div className="flex items-center gap-2 label-eyebrow">
                            <Landmark size={14} color="#C9A84C" />
                            {t("Turkish Law Layer", "Türk Hukuku Katmanı")}
                        </div>
                        <h2 className="font-display text-3xl lg:text-4xl mt-3 leading-[1.1] tracking-tight">
                            {t("EU and Türkiye, in one workspace.", "AB ve Türkiye, tek platformda.")}
                        </h2>
                        <p className="mt-5 text-base leading-relaxed text-[#0D1B3E]/75">
                            {lang === "tr"
                                ? "AB'deki müvekkillere hizmet veren veya KVKK ile GDPR uyumunu birlikte yürütmesi gereken hukuk büroları, şirket içi hukukçular ve veri koruma görevlileri için tasarlandı. Türk hukuku yanıtları, mevzuat.gov.tr, Resmî Gazete ve yüksek mahkeme veritabanları gibi resmi kaynaklarda doğrulanır."
                                : "For firms and in-house teams advising across both legal orders. Turkish law answers are grounded in verified official sources: mevzuat.gov.tr, the Resmi Gazete and the databases of the Turkish high courts and authorities."}
                        </p>
                        <p className="mt-3 text-xs text-[#0D1B3E]/55">
                            {t("Legal information, not legal advice.", "Hukuki bilgidir, hukuki tavsiye değildir.")}
                        </p>
                        <div className="mt-7">
                            <Link to="/signup" className="btn-primary" data-testid="turkiye-section-cta"
                                onClick={() => trackPublic("landing_cta_click", { section: "turkiye_layer", lang })}>
                                {t("Start Free Trial", "Ücretsiz Denemeyi Başlat")} <ArrowRight size={16} />
                            </Link>
                        </div>
                    </div>
                    <div className="lg:col-span-7">
                        <ul className="grid gap-4 sm:grid-cols-2">
                            {items.map((it, i) => (
                                <li key={i} className="bg-white/70 border border-[#0D1B3E]/10 p-5 text-sm leading-relaxed text-[#0D1B3E]/85">
                                    {it}
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </div>
        </section>
    );
}

function ModuleCard({ icon: Icon, title, body, delay }) {
    return (
        <Reveal delay={delay} className="h-full">
            <div className="bg-[#F5F0E8] h-full p-8 hover:bg-white transition-colors duration-300">
                <Icon size={22} strokeWidth={1.6} color="#C9A84C" />
                <h3 className="font-display text-xl tracking-tight mt-5">{title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-[#0D1B3E]/75">{body}</p>
            </div>
        </Reveal>
    );
}

function Step({ n, icon: Icon, title, body, delay }) {
    return (
        <Reveal delay={delay}>
            <div className="border-t border-[#C9A84C]/40 pt-6">
                <div className="flex items-center justify-between">
                    <span className="font-display text-lg" style={{ color: "#C9A84C" }}>{n}</span>
                    <Icon size={20} strokeWidth={1.6} color="#C9A84C" />
                </div>
                <h3 className="font-display text-xl tracking-tight mt-4">{title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-[#F5F0E8]/75">{body}</p>
            </div>
        </Reveal>
    );
}
